import { IssueMessageType, IssueType, Severity } from '../../graphql/enums';
import { Media } from '../../media/domain/media';
import { WorkspaceAsset } from '../../assets/domain/workspaceAsset';

export const ASSET_CHAT_STREAM_FILTERS = [
  'all',
  'messages',
  'activities',
  'urgent',
  'pending',
  'solved',
] as const;

export type AssetChatStreamFilter = (typeof ASSET_CHAT_STREAM_FILTERS)[number];

/**
 * What an asset chat is about: one asset as it appears in ONE workspace. An
 * `Asset` has a `workspaceasset` row per workspace it is shared into, and
 * `Issue.asset` points at the global asset, so a chat keyed on the bare asset
 * would merge every workspace's conversations into one feed. The feed
 * addresses the WorkspaceAsset through `findIssues` as the (asset, workspace)
 * pair — `Issue` carries no `workspaceAsset` reference.
 */
export interface AssetChatSubject {
  workspaceAssetId: string;
  assetId: string;
  workspaceId: string;
  /**
   * The pinned `workspaceasset` row itself, in the shape `hasAssetAccess`
   * reads. Exposed so callers gate on the workspace the chat is actually
   * scoped to, rather than re-deriving it from `useWorkspaceAsset`, whose
   * `items[0]` is an arbitrary pick across a multi-workspace asset — the
   * exact thing this subject exists to avoid.
   */
  workspaceAsset: WorkspaceAsset;
}

interface AssetChatFeedActor {
  id: string;
  name?: { first?: string; last?: string };
}

/** One `FindIssueItem` as selected by the chat feed query. */
export interface AssetChatFeedItem {
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  type: IssueType;
  isPinned?: boolean;
  asset?: {
    id?: string;
    name?: string;
    coverImage?: {
      id?: string;
      url?: string;
      extension?: string;
      status?: string;
      type?: string;
    };
  };
  workspace?: { id?: string; name?: string };
  labels?: { id: string; name: string }[];
  createdAt: string;
  effectiveCreationDate?: string;
  createdBy?: AssetChatFeedActor;
  updatedAt: string;
  updatedBy?: AssetChatFeedActor;
  userMentions?: AssetChatFeedActor[];
  /**
   * The row's body: the activity's earliest top-level, non-deleted message —
   * `latestMessage` is the wrong end of the thread for a row representing the
   * post itself. Null on most activities on a routine-heavy asset (a routine
   * or alert path creates the Issue with no message); the row falls back to
   * `title` in that case.
   */
  originMessage?: {
    id: string;
    text?: string;
    i18n?: unknown;
    type: IssueMessageType;
    createdBy: AssetChatFeedActor;
    media?: Media[];
  };
  replyCount?: number;
  /**
   * Set only on a row the composer wrote locally that the server has not
   * acknowledged yet (see `useCreateChatActivity`). Never present on a row
   * from `findIssues`. Its `id` is a placeholder, so a pending row must not
   * offer a route into a thread that does not exist yet.
   */
  isPending?: boolean;
}

export interface AssetChatFeedPage {
  items: AssetChatFeedItem[];
  count: number;
  offset: number;
  limit: number;
}
